import { useEffect, useMemo } from 'react';
import { useAnalysisData } from '../../contexts/AnalysisDataContext';
import { AnalysisRange } from './useAnalysisRange';
import { Granularity } from '../../utils/analytics/types';
import {
  feeEstimateTotal,
  feeTrendEstimate,
  losDistribution,
  medianLos,
  siteAddRate,
  sitePopularity,
  siteTypeTrend,
} from '../../utils/analytics/productMetrics';

export function useProductAnalysis(range: AnalysisRange, granularity: Granularity) {
  const { stays, sites, siteTypes, overageFees, loading, error, ensureRange } = useAnalysisData();

  useEffect(() => {
    ensureRange(range.from, range.to);
  }, [range.from, range.to, ensureRange]);

  return useMemo(() => {
    const { from, to } = range;
    const typeIdBySiteId = new Map(sites.filter(s => s.type_id != null).map(s => [s.id, s.type_id!]));
    const typeNameById = new Map(siteTypes.map(t => [t.id, t.name]));
    const feePoints = feeTrendEstimate(stays, overageFees, from, to, granularity);
    const popularity = sitePopularity(stays, from, to);

    return {
      loading,
      error,
      losBuckets: losDistribution(stays, from, to),
      medianLos: medianLos(stays, from, to),
      popularity,
      topSite: popularity[0] ?? null,
      typeTrend: siteTypeTrend(stays, from, to, granularity, typeIdBySiteId, typeNameById),
      siteAddRate: siteAddRate(stays, from, to),
      feePoints,
      feeTotal: feeEstimateTotal(feePoints),
      hasFeeConfig: overageFees.some(f => f.active),
    };
  }, [stays, sites, siteTypes, overageFees, range, granularity, loading, error]);
}
